/**
 * Houdt het ontvangersveld bij de mail en de contactenlijst gelijk.
 *
 * Het zoekveld voor CC/BCC geeft telkens de volledige nieuwe lijst terug. Hier wordt
 * daaruit afgeleid wat er met `state.contacts` moet gebeuren, zodat wie je bij de mail
 * toevoegt ook links in de contactenlijst verschijnt en omgekeerd.
 *
 * Bewust een pure functie: de afwegingen (bestaat de persoon al? stond die eerder in de
 * lijst?) zijn de kern van de tweerichtingssynchronisatie en zijn zo los te testen.
 */
import { Contact, RecipientType } from '../types';

export interface RecipientInput {
  name: string;
  email: string;
}

export type RecipientSyncAction =
  /** Bestaat al in de lijst: verhuis naar de andere rol i.p.v. dubbel te staan. */
  | { type: 'set-role'; contactId: string; recipientType: RecipientType }
  /** Eerder weggeklikt: terughalen i.p.v. een tweede exemplaar aanmaken. */
  | { type: 'restore'; contact: Contact; recipientType: RecipientType }
  /** Volledig nieuw adres. */
  | { type: 'add'; contacts: Contact[] }
  | { type: 'remove'; contactId: string };

const sleutel = (email?: string) => (email || '').trim().toLowerCase();

const zoekOpEmail = (contacten: Contact[], email: string) =>
  contacten.find((contact) => sleutel(contact.email) === sleutel(email));

export function maakOntvangerContact(
  recipient: RecipientInput,
  recipientType: RecipientType,
): Contact {
  const email = recipient.email.trim();
  return {
    id: email,
    name: recipient.name || email,
    firstName: recipient.name || email,
    lastName: '',
    email,
    resourceId: email,
    phoneMobile: '',
    units: [],
    buildingId: '',
    buildingName: '',
    unitId: '',
    unitNumber: '',
    recipientType,
  };
}

export function planRecipientChange(params: {
  /** Rol van het veld waarin de wijziging gebeurde. */
  recipientType: RecipientType;
  /** Ontvangers die dat veld nu toont. */
  huidige: Contact[];
  /** Ontvangers die het veld na de bewerking moet tonen. */
  nieuweLijst: RecipientInput[];
  /** Volledige contactenlijst — ook de contacten in de andere velden. */
  contacts: Contact[];
  excludedContacts: Contact[];
}): RecipientSyncAction[] {
  const { recipientType, huidige, nieuweLijst, contacts, excludedContacts } = params;

  const nieuweAdressen = new Set(nieuweLijst.map((r) => sleutel(r.email)));
  const huidigeAdressen = new Set(huidige.map((c) => sleutel(c.email)));

  const toegevoegd = nieuweLijst.filter((r) => !huidigeAdressen.has(sleutel(r.email)));

  // Per bewerking verandert er precies één ontvanger (toevoegen of een chip sluiten),
  // dus toevoegen en verwijderen hoeven niet gecombineerd te worden.
  if (toegevoegd.length) {
    const acties: RecipientSyncAction[] = [];
    const nieuweContacten: Contact[] = [];

    toegevoegd.forEach((recipient) => {
      const bestaand = zoekOpEmail(contacts, recipient.email);
      if (bestaand) {
        acties.push({ type: 'set-role', contactId: bestaand.id, recipientType });
        return;
      }

      const uitgesloten = zoekOpEmail(excludedContacts, recipient.email);
      if (uitgesloten) {
        acties.push({ type: 'restore', contact: uitgesloten, recipientType });
        return;
      }

      nieuweContacten.push(maakOntvangerContact(recipient, recipientType));
    });

    if (nieuweContacten.length) acties.push({ type: 'add', contacts: nieuweContacten });
    return acties;
  }

  return huidige
    .filter((contact) => !nieuweAdressen.has(sleutel(contact.email)))
    .map((contact) => ({ type: 'remove', contactId: contact.id }));
}
